import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useState,
  useRef,
  type ReactNode,
} from "react"
import type { IExecutionStep } from "../../interfaces/execution"
import type {
  ISimulatorState,
  ISimulatorCallbacks,
} from "../../interfaces/simulator"
import { initialState, simulatorReducer } from "../../interfaces/simulator"
import { ExecutionEngine } from "../../engine/ExecutionEngine"
import { parse } from "../../parser"
import { CodeGenerator } from "../../engine/CodeGenerator"
import { userFacingExecutionError } from "../../engine/errors"
import type {
  IRawSubroutineDefinition,
  IParserInputNode,
  IParserInputEdge,
} from "../../parser/types"

type Tab = "trace" | "explain" | "code"

interface ISimulatorContextValue {
  state: ISimulatorState
  activeTab: Tab
  setActiveTab: (tab: Tab) => void
  canStepBack: boolean
  canStepForward: boolean
  canHistoryForward: boolean
  start: () => void
  stepForward: () => void
  stepBack: () => void
  stepHistoryForward: () => void
  runAll: () => void
  stop: () => void
  reset: () => void
  setSpeed: (speed: number) => void
  setCode: (js: string, ts: string) => void
  goToStep: (index: number) => void
  submitInput: (value: string) => void
  cancelInput: () => void
  setEngine: (engine: ExecutionEngine) => void
  loadDiagram: (
    nodes: IParserInputNode[],
    edges: IParserInputEdge[],
    options?: { subroutines?: IRawSubroutineDefinition[] },
  ) => { ok: true } | { ok: false; error: string }
  loadAndStartDiagram: (
    nodes: IParserInputNode[],
    edges: IParserInputEdge[],
    options?: { subroutines?: IRawSubroutineDefinition[] },
  ) => { ok: true } | { ok: false; error: string }
}

const SimulatorContext = createContext<ISimulatorContextValue | null>(null)

/**
 * The desk check row for an input waiting for `variable`. A block reading
 * several variables reports the one it just stored, so the row is rebuilt for
 * the variable being asked for.
 */
function waitingRow(step: IExecutionStep, variable: string): IExecutionStep {
  return {
    ...step,
    log: `Solicitando ${variable}.`,
    explanation: `Aguardando valor para '${variable}'.`,
    changes: [],
    nextHint: "Informe o valor.",
    waitingForInput: true,
    inputEntered: false,
    inputVariable: variable,
  }
}

export function SimulatorProvider({
  children,
  callbacks,
}: {
  children: ReactNode
  callbacks?: ISimulatorCallbacks
}) {
  const [state, dispatch] = useReducer(simulatorReducer, initialState)
  const [activeTab, setActiveTab] = useState<Tab>("trace")
  const engineRef = useRef<ExecutionEngine | null>(null)

  const setEngine = useCallback((engine: ExecutionEngine) => {
    engineRef.current = engine
  }, [])

  const syncFromEngine = useCallback(() => {
    const engine = engineRef.current
    if (!engine) return
    const execution = engine.getCurrentState()
    dispatch({
      type: "SYNC_EXECUTION",
      steps: execution.steps,
      currentStepIndex: engine.currentStepIndex,
      outputs: execution.outputs,
      isFinished: execution.finished,
    })
  }, [])

  const loadDiagram = useCallback(
    (
      nodes: IParserInputNode[],
      edges: IParserInputEdge[],
      options?: { subroutines?: IRawSubroutineDefinition[] },
    ) => {
      try {
        const graph = parse(nodes, edges, { subroutines: options?.subroutines })
        if (!graph.startNodeId) {
          return { ok: false, error: "Nenhum bloco de Início: coloque um para executar o algoritmo." }
        }
        const engine = new ExecutionEngine(graph)
        setEngine(engine)
        const codeGen = new CodeGenerator(graph)
        const js = codeGen.generate({ lang: "js" })
        const ts = codeGen.generate({ lang: "ts" })
        dispatch({ type: "SET_CODE", js, ts })
        return { ok: true }
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Erro ao carregar diagrama"
        return { ok: false, error: msg }
      }
    },
    [setEngine],
  )

  const canStepBack =
    state.isStarted &&
    state.currentStepIndex > 0 &&
    !state.isRunning &&
    !state.awaitingInput
  const canStepForward =
    state.isStarted &&
    !state.isFinished &&
    !state.isRunning &&
    !state.awaitingInput
  const canHistoryForward =
    state.isStarted &&
    state.currentStepIndex < state.steps.length - 1 &&
    !state.isRunning &&
    !state.awaitingInput

  const executeNextStep = useCallback(
    (input?: string) => {
      const engine = engineRef.current
      if (!engine) {
        dispatch({ type: "SET_ERROR", error: "Nenhum diagrama carregado." })
        return
      }

      try {
        const step = engine.step(input)
        if (!step) {
          const execution = engine.getCurrentState()
          if (execution.error) {
            dispatch({ type: "SET_ERROR", error: userFacingExecutionError(execution.error) })
          } else {
            dispatch({ type: "FINISH" })
          }
          return
        }

        if (step.waitingForInput) {
          if (step.inputEntered) syncFromEngine()
          const variable = step.inputVariable ?? step.nodeLabel
          dispatch({
            type: "INPUT_REQUESTED",
            prompt: step.inputPrompt ?? `Valor para '${step.nodeLabel}':`,
            variable,
            inputType: step.inputType ?? "caractere",
            step: waitingRow(step, variable),
          })
        } else {
          syncFromEngine()
        }
      } catch (e) {
        // The steps that ran before the error still belong in the desk check.
        syncFromEngine()
        dispatch({ type: "SET_ERROR", error: userFacingExecutionError(e) })
      }
    },
    [syncFromEngine],
  )

  const start = useCallback(() => {
    dispatch({ type: "START" })
    callbacks?.onStart?.()

    const engine = engineRef.current
    if (!engine) {
      dispatch({ type: "SET_ERROR", error: "Nenhum diagrama carregado." })
      return
    }

    try {
      const step = engine.start()
      if (step) {
        syncFromEngine()
      } else {
        const execution = engine.getCurrentState()
        if (execution.error) {
          dispatch({ type: "SET_ERROR", error: userFacingExecutionError(execution.error) })
        }
      }
    } catch (e) {
      syncFromEngine()
      dispatch({ type: "SET_ERROR", error: userFacingExecutionError(e) })
    }
  }, [callbacks, syncFromEngine])

  const stepForward = useCallback(() => {
    callbacks?.onStepForward?.()
    executeNextStep()
  }, [callbacks, executeNextStep])

  const submitInput = useCallback(
    (value: string) => {
      dispatch({ type: "SUBMIT_INPUT" })
      callbacks?.onInputSubmit?.(value)
      executeNextStep(value)
    },
    [callbacks, executeNextStep],
  )

  const cancelInput = useCallback(() => {
    dispatch({ type: "SUBMIT_INPUT" })
    // Asking for a value may have hidden steps ahead in the history; the
    // engine still has them.
    syncFromEngine()
    dispatch({ type: "STOP" })
    callbacks?.onInputCancel?.()
  }, [callbacks, syncFromEngine])

  const stepBack = useCallback(() => {
    if (state.currentStepIndex <= 0) return
    engineRef.current?.goToStep(state.currentStepIndex - 1)
    syncFromEngine()
    callbacks?.onStepBack?.()
  }, [callbacks, state.currentStepIndex, syncFromEngine])

  const stepHistoryForward = useCallback(() => {
    if (state.currentStepIndex >= state.steps.length - 1) return
    engineRef.current?.goToStep(state.currentStepIndex + 1)
    syncFromEngine()
  }, [state.currentStepIndex, state.steps.length, syncFromEngine])

  const runAll = useCallback(() => {
    dispatch({ type: "RUN_ALL" })
    callbacks?.onRunAll?.()
  }, [callbacks])

  const stop = useCallback(() => {
    dispatch({ type: "STOP" })
    callbacks?.onStop?.()
  }, [callbacks])

  const reset = useCallback(() => {
    dispatch({ type: "RESET" })
    engineRef.current?.reset()
    callbacks?.onReset?.()
  }, [callbacks])

  const loadAndStartDiagram = useCallback(
    (
      nodes: IParserInputNode[],
      edges: IParserInputEdge[],
      options?: { subroutines?: IRawSubroutineDefinition[] },
    ) => {
      reset()
      const result = loadDiagram(nodes, edges, options)
      if (!result.ok) {
        dispatch({ type: "SET_ERROR", error: result.error })
        return result
      }

      start()
      return result
    },
    [loadDiagram, reset, start],
  )

  const setSpeed = useCallback((speed: number) => {
    dispatch({ type: "SET_SPEED", speed })
  }, [])

  const setCode = useCallback((js: string, ts: string) => {
    dispatch({ type: "SET_CODE", js, ts })
  }, [])

  const goToStep = useCallback(
    (index: number) => {
      if (index < 0 || index >= state.steps.length) return
      if (engineRef.current?.goToStep(index)) syncFromEngine()
    },
    [state.steps.length, syncFromEngine],
  )

  useEffect(() => {
    if (!state.isRunning || state.awaitingInput || state.isFinished) return

    const timer = window.setTimeout(() => {
      executeNextStep()
    }, state.speed)

    return () => window.clearTimeout(timer)
  }, [
    state.isRunning,
    state.awaitingInput,
    state.isFinished,
    state.speed,
    state.currentStepIndex,
    state.steps.length,
    executeNextStep,
  ])

  return (
    <SimulatorContext.Provider
      value={{
        state,
        activeTab,
        setActiveTab,
        canStepBack,
        canStepForward,
        canHistoryForward,
        start,
        stepForward,
        stepBack,
        stepHistoryForward,
        runAll,
        stop,
        reset,
        setSpeed,
        setCode,
        goToStep,
        submitInput,
        cancelInput,
        setEngine,
        loadDiagram,
        loadAndStartDiagram,
      }}
    >
      {children}
    </SimulatorContext.Provider>
  )
}

export function useSimulator(): ISimulatorContextValue {
  const ctx = useContext(SimulatorContext)
  if (!ctx) {
    throw new Error("useSimulator must be used within SimulatorProvider")
  }
  return ctx
}
